import React, { useState, useMemo } from 'react';
import '../../styles/accessibility.css';

/*
  Ethics › Onboarding Guide
  Structured landing for first‑time users entering the Ethics department.
  Features:
   - Hero with gradient + ethics tagline
   - Role selector (Supply Chain, Compliance, ESG Lead)
   - Adaptive checklist / journey steps per role
   - Quick links & learning tiles
   - Ethics & ESG Principles highlight band
*/

const ROLES = [
  {
    id: 'supply-chain',
    label: 'Supply Chain Ethics Manager',
    color: 'from-emerald-600 to-teal-600',
    kpis: ['Supplier Compliance Score', 'Ethical Sourcing Rate', 'Audit Coverage'],
    steps: [
      'Review supplier ecosystem dashboard',
      'Configure ethical sourcing criteria',
      'Run initial supplier assessment',
      'Set up performance monitoring alerts'
    ]
  },
  {
    id: 'compliance',
    label: 'Ethics & Compliance Officer',
    color: 'from-blue-600 to-indigo-600',
    kpis: ['Compliance Rate', 'Policy Adherence', 'Risk Mitigation'],
    steps: [
      'Examine cultural framework guidelines',
      'Review community engagement metrics',
      'Validate impact assessment protocols',
      'Export first ethics compliance report'
    ]
  },
  {
    id: 'esg-lead',
    label: 'ESG Strategy Lead',
    color: 'from-purple-600 to-pink-600',
    kpis: ['ESG Impact Score', 'Stakeholder Engagement', 'Global Reach'],
    steps: [
      'Analyze localization effectiveness',
      'Review supplier onboarding process',
      'Set cultural impact benchmarks',
      'Configure communication hub preferences'
    ]
  }
];

const QUICK_LINKS = [
  { title: 'Supply Chain Ethics Overview', path: '/ethics/supply-chain-ethics/overview', icon: 'bi-diagram-3' },
  { title: 'Supplier Dashboard', path: '/ethics/supplier-ecosystem/supplier-dashboard', icon: 'bi-speedometer2' },
  { title: 'Cultural Framework', path: '/ethics/cultural-and-global-impact/cultural-framework', icon: 'bi-globe' },
  { title: 'Impact Assessment', path: '/ethics/cultural-and-global-impact/impact-assessment', icon: 'bi-clipboard-data' },
  { title: 'Ethical Sourcing', path: '/ethics/supply-chain-ethics/ethical-sourcing', icon: 'bi-shield-check' },
  { title: 'Community Engagement', path: '/ethics/cultural-and-global-impact/community-engagement', icon: 'bi-people' }
];

export default function EthicsOnboardingGuide(){
  const [role, setRole] = useState(ROLES[0]?.id);
  const active = useMemo(()=> ROLES.find(r=> r.id===role), [role]);
  const [completed, setCompleted] = useState(new Set());
  function toggle(step){
    setCompleted(prev=>{ const n = new Set(prev); n.has(step) ? n.delete(step) : n.add(step); return n; });
  }

  return (
    <div className="min-h-full space-y-10">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900 via-teal-800 to-green-700 text-white p-10 shadow-lg">
        <div className="max-w-4xl relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">Ethics Department Onboarding</h1>
          <p className="text-white/80 text-lg leading-relaxed mb-6">
            Welcome to the Ethics Department. Here, we orchestrate ethical transformation across supply chains, cultural impact, and global engagement. Every decision you make ripples through communities, suppliers, and stakeholders. Let's align your journey with integrity, transparency, and sustainable impact.
          </p>
          <div className="flex flex-wrap gap-3">
            {ROLES.map(r=> (
              <button
                key={r.id}
                onClick={()=> setRole(r.id)}
                className={[
                  'px-4 py-2 rounded-full text-sm font-medium transition-all shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/60',
                  role===r.id ? 'bg-white text-emerald-800' : 'bg-white/10 hover:bg-white/20 text-white'
                ].join(' ')}
                aria-pressed={role===r.id}
              >{r.label}</button>
            ))}
          </div>
        </div>
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{backgroundImage:'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.25), transparent 60%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.18), transparent 55%)'}} />
      </section>

      {/* Role Snapshot & Checklist */}
      <section className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-slate-200 bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-1 text-slate-800">{active.label} Journey</h2>
            <p className="text-slate-600 text-sm mb-4">Complete the high‑impact activation steps below. Progress auto‑persists in this session.</p>
            <ol className="space-y-3 list-decimal list-inside">
              {active.steps.map(step=>{
                const done = completed.has(step);
                return (
                  <li key={step} className="pl-1">
                    <button
                      onClick={()=> toggle(step)}
                      className={[ 'group w-full text-left flex items-start gap-3 rounded-lg px-3 py-2 transition-colors', done ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-slate-50 border border-transparent'].join(' ')}
                      aria-pressed={done}
                    >
                      <span className={['mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold', done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600 group-hover:bg-slate-300'].join(' ')}>
                        {done ? '✓' : active.steps.indexOf(step)+1}
                      </span>
                      <span className={['flex-1 text-sm', done ? 'line-through text-emerald-700' : 'text-slate-700'].join(' ')}>{step}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <div className="mt-5 flex items-center gap-3 text-sm">
              <div className="flex h-2 rounded bg-slate-200 w-40 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500" style={{width: (completed.size/active.steps.length*100)+'%'}} />
              </div>
              <span className="text-slate-600">{completed.size}/{active.steps.length} complete</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {active.kpis.map(k=> (
              <div key={k} className="relative rounded-xl border border-slate-200 bg-white p-4 shadow-sm overflow-hidden group">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-br from-slate-50 to-emerald-50" />
                <div className="relative">
                  <p className="text-xs font-medium tracking-wide text-slate-500 uppercase mb-1">Focus KPI</p>
                  <h3 className="text-slate-800 font-semibold text-sm mb-2">{k}</h3>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 text-[11px] px-2 py-1 font-medium">Simulated</span>
                    <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 text-[11px] px-2 py-1 font-medium">Track</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <aside className="space-y-6">
          <div className="border border-slate-200 rounded-xl bg-white shadow-sm p-5">
            <h3 className="text-slate-800 font-semibold mb-3 text-sm tracking-wide uppercase">Quick Launch</h3>
            <ul className="space-y-2">
              {QUICK_LINKS.map(link=> (
                <li key={link.path}>
                  <a href={link.path} className="group flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors">
                    <i className={`bi ${link.icon} text-slate-400 group-hover:text-emerald-500`} />
                    <span className="flex-1">{link.title}</span>
                    <span className="text-[10px] text-slate-400 group-hover:text-emerald-500">›</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-emerald-200 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 p-5 shadow-sm">
            <h3 className="text-emerald-800 font-semibold mb-2 text-sm tracking-wide uppercase">Ethics & ESG Pillars</h3>
            <ul className="text-xs text-slate-600 space-y-1">
              <li><strong className="text-slate-800">Transparency:</strong> Open supplier communication & traceability.</li>
              <li><strong className="text-slate-800">Fairness:</strong> Equitable treatment across supply chains & communities.</li>
              <li><strong className="text-slate-800">Accountability:</strong> Measurable impact & stakeholder responsibility.</li>
              <li><strong className="text-slate-800">Sustainability:</strong> Long-term environmental & social value creation.</li>
            </ul>
          </div>
        </aside>
      </section>

      {/* Learning Tiles */}
      <section>
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Explore Ethics Domains</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <LearningTile title="Supply Chain Ethics" desc="Assess supplier compliance, ethical sourcing standards & audit coverage." path="/ethics/supply-chain-ethics/overview" gradient="from-emerald-600 to-teal-500" />
          <LearningTile title="Cultural & Global Impact" desc="Measure localization, community engagement & cultural framework effectiveness." path="/ethics/cultural-and-global-impact/localization" gradient="from-blue-600 to-indigo-500" />
          <LearningTile title="Supplier Ecosystem" desc="Manage supplier dashboard, onboarding & performance monitoring." path="/ethics/supplier-ecosystem/supplier-dashboard" gradient="from-purple-600 to-pink-500" />
        </div>
      </section>

      <footer className="pt-4 pb-10 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} Trust Tech Studio • Ethics Onboarding Fabric (Simulated Data)
      </footer>
    </div>
  );
}

function LearningTile({ title, desc, path, gradient }){
  return (
    <a href={path} className="group relative block rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-br ${gradient} mix-blend-multiply`} />
      <div className="relative p-6 space-y-3">
        <h3 className="text-slate-800 group-hover:text-white font-semibold tracking-tight text-base transition-colors">{title}</h3>
        <p className="text-slate-600 group-hover:text-white/90 text-sm leading-relaxed">{desc}</p>
        <span className="inline-flex items-center gap-1 text-emerald-600 group-hover:text-white text-xs font-medium">Explore <span className="translate-x-0 group-hover:translate-x-0.5 transition-transform">›</span></span>
      </div>
    </a>
  );
}
